import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api from '@src/api.js';
import { getCompanyProfile, getCapiqShareholdersUrl } from '@src/config/server-config.js';

// Shape returned by GET /api/v1/capiq/company/{rp_id}/shareholders
export interface CapiqHolding {
  company_id: number;
  company_name: string;
  percentage: number;
  shares: number;
  value: number;
}

export interface CapiqInvestorHoldings {
  total_holdings: number;
  holdings: CapiqHolding[];
}

export interface CapiqShareholder {
  company_id: number;
  rank: number;
  investor_id: number;
  investor_name: string;
  percentage: number;
  shares: number;
  value: number;
  funds: number;
  investor_type: string;
  other_holdings?: CapiqInvestorHoldings;
}

// Company-wide ownership split by investor type (all investors, not just the top 25).
export interface CapiqOwnershipByType {
  investor_type: string;
  percentage: number;
}

export interface CapiqShareholdersResponse {
  top_shareholders: CapiqShareholder[];
  ownership_by_type: CapiqOwnershipByType[];
  // Company-wide institutional ownership %, computed by the backend.
  institutional_ownership_percentage: number;
}

export interface OwnershipOverviewState {
  data: CapiqShareholdersResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: OwnershipOverviewState = {
  data: null,
  loading: false,
  error: null,
};

// The shareholders endpoint is keyed on rp_id, but the company page only has pid,
// so resolve pid -> rp_id via the company profile (RPCompany_ID) first.
export const GetOwnershipOverviewData = async (pid: string): Promise<CapiqShareholdersResponse> => {
  const profileResponse = await api.post(getCompanyProfile, { pid: +pid });
  const rpCompanyId = profileResponse?.data?.RPCompany_ID;

  if (!rpCompanyId) {
    throw new Error('RPCompanyID not found for this company');
  }

  // api.get returns { data: <body> }, and the capiq body is enveloped as
  // { data: <payload>, execution_time_ms, cache_hit }, so unwrap twice (.data.data).
  const url = getCapiqShareholdersUrl(rpCompanyId);
  const response = await api.get(url);
  const body = response?.data;

  if (body?.success === false) {
    throw new Error(typeof body.data === 'string' ? body.data : 'Failed to load ownership data');
  }

  const payload = (body?.data ?? {
    top_shareholders: [],
    ownership_by_type: [],
    institutional_ownership_percentage: 0,
  }) as CapiqShareholdersResponse;
  return payload;
};

export const fetchOwnershipOverview = createAsyncThunk(
  'ownershipOverview/fetch',
  async ({ companyId }: { companyId: string }) => {
    return GetOwnershipOverviewData(companyId);
  },
);

const OwnershipOverviewSlice = createSlice({
  name: 'ownershipOverview',
  initialState,
  reducers: {
    resetOwnershipOverview: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOwnershipOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOwnershipOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload ?? null;
        state.error = null;
      })
      .addCase(fetchOwnershipOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to load ownership data';
      });
  },
});

export const { resetOwnershipOverview } = OwnershipOverviewSlice.actions;

export default OwnershipOverviewSlice.reducer;
